import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import { gymnasiumService } from '../../services/gymnasiumService';
import { Gauntlet, GauntletScenario } from '../../types/gymnasium';
import { PersonaMarkdown } from '../personas/PersonaMarkdown';
import api from '../../services/api';
import { FiArrowLeft, FiPlay, FiChevronDown, FiChevronRight, FiEdit } from 'react-icons/fi';
import GauntletEditor from './GauntletEditor';

interface Props {
  gauntletId: string;
}

const GauntletDetail: React.FC<Props> = ({ gauntletId }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [gauntlet, setGauntlet] = useState<Gauntlet | null>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [personas, setPersonas] = useState<any[]>([]);
  const [selectedPersona, setSelectedPersona] = useState('');
  const [judgeMode, setJudgeMode] = useState('auto');
  const [starting, setStarting] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [g, r] = await Promise.all([
          gymnasiumService.getGauntlet(gauntletId),
          gymnasiumService.listRuns(undefined, gauntletId),
        ]);
        setGauntlet(g);
        setRuns(r);
      } catch (err) {
        console.error('Failed to load gauntlet:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [gauntletId, editing]);

  const loadPersonas = async () => {
    try {
      const resp = await api.get('/personas?limit=500');
      const data = resp.data?.personas || resp.data || [];
      setPersonas(data.filter((p: any) => p.prompt || p.data?.data?.engineering_profile?.current_state?.system_prompt));
    } catch (err) {
      console.error('Failed to load personas:', err);
    }
  };

  const handleStartRun = async () => {
    if (!selectedPersona) return;
    setStarting(true);
    try {
      const result = await gymnasiumService.startRun(gauntletId, selectedPersona, judgeMode);
      navigate(`/gymnasium/runs/${result.run_id}`);
    } catch (err) {
      console.error('Failed to start run:', err);
      setStarting(false);
    }
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  if (loading) {
    return <div className="space-y-4 animate-pulse"><div className={`h-48 rounded-xl ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} /></div>;
  }

  if (!gauntlet) {
    return <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Gauntlet not found</div>;
  }

  if (editing) {
    return <GauntletEditor editGauntlet={gauntlet} onSaved={() => setEditing(false)} onCancel={() => setEditing(false)} />;
  }

  const scenarios: GauntletScenario[] = gauntlet.scenarios || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/gymnasium/gauntlets')} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <FiArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
              {gauntlet.name.replace(/_/g, ' ')}
            </h1>
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {scenarios.length} scenarios · v{gauntlet.version || 1}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(true)} className={`px-3 py-2 text-sm rounded-lg flex items-center gap-1.5 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
            <FiEdit className="w-3.5 h-3.5" /> Edit
          </button>
          <button
            onClick={() => { setShowRunModal(true); loadPersonas(); }}
            className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 transition-all flex items-center gap-2"
          >
            <FiPlay className="w-4 h-4" /> Run
          </button>
        </div>
      </div>

      {/* Description + Tags */}
      {(gauntlet.description || gauntlet.tags?.length > 0) && (
        <div className={sectionClass}>
          {gauntlet.description && <div className="mb-2"><PersonaMarkdown content={gauntlet.description} size="small" /></div>}
          {gauntlet.tags?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {gauntlet.tags.map(t => (
                <span key={t} className="px-2 py-0.5 text-[10px] rounded bg-amber-600/20 text-amber-300">{t}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scenarios */}
      <div className={sectionClass}>
        <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Scenarios</h2>
        <div className="space-y-2">
          {scenarios.map((s, i) => {
            const isExpanded = expandedScenario === s.id;
            return (
              <div key={s.id} className={`rounded-lg border ${darkMode ? 'border-gray-700/40 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                <button
                  onClick={() => setExpandedScenario(isExpanded ? null : s.id)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                >
                  {isExpanded ? <FiChevronDown className="w-3.5 h-3.5 shrink-0" /> : <FiChevronRight className="w-3.5 h-3.5 shrink-0" />}
                  <span className={`text-sm font-medium flex-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {i + 1}. {s.label}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                    {s.category} · {s.difficulty || 'medium'} · {s.max_score}pts
                  </span>
                </button>
                {isExpanded && (
                  <div className={`px-3 pb-3 border-t ${darkMode ? 'border-gray-700/30' : 'border-gray-200'}`}>
                    <div className="mt-2 mb-3"><PersonaMarkdown content={s.prompt} size="small" /></div>
                    <div>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Criteria:</span>
                      <ul className="mt-1 space-y-1">
                        {s.criteria.map((c, ci) => (
                          <li key={ci} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>• {c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Run History */}
      {runs.length > 0 && (
        <div className={sectionClass}>
          <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Run History</h2>
          <div className="space-y-2">
            {runs.map((r: any) => (
              <button
                key={r.run_id}
                onClick={() => navigate(`/gymnasium/runs/${r.run_id}`)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                  darkMode ? 'bg-gray-800/40 hover:bg-gray-700/40' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <PersonaBadge personaId={r.persona_id} personaName={r.persona_name || r.persona_id?.slice(0, 8)} size="xs" clickable={false} />
                <span className={`text-sm flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {r.persona_name || r.persona_id?.slice(0, 12)}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  r.status === 'completed' ? 'bg-green-600/20 text-green-400' :
                  r.status === 'failed' ? 'bg-red-600/20 text-red-400' :
                  'bg-amber-600/20 text-amber-400'
                }`}>
                  {r.status === 'completed' ? `${r.pass_rate != null ? (r.pass_rate * 100).toFixed(0) + '%' : 'done'}` : r.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Run Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 w-[480px] max-w-full shadow-xl`}>
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Run Gauntlet: {gauntlet.name.replace(/_/g, ' ')}
            </h3>

            <div className="mb-4">
              <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Select Persona</label>
              <select
                value={selectedPersona}
                onChange={e => setSelectedPersona(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg text-sm ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'
                } border`}
              >
                <option value="">Choose a persona...</option>
                {personas.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Judge Mode</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setJudgeMode('auto')}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                    judgeMode === 'auto'
                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                      : darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-500'
                  }`}
                >
                  Auto (LLM judges)
                </button>
                <button
                  onClick={() => setJudgeMode('manual')}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                    judgeMode === 'manual'
                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                      : darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-500'
                  }`}
                >
                  Manual (you judge)
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowRunModal(false)} className={`px-4 py-2 text-sm rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
                Cancel
              </button>
              <button
                onClick={handleStartRun}
                disabled={!selectedPersona || starting}
                className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 flex items-center gap-2"
              >
                <FiPlay className="w-4 h-4" />
                {starting ? 'Starting...' : 'Start Run'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GauntletDetail;
